import {z} from '@hono/zod-openapi';

import {ISSUE_REPORT_DIAGNOSTICS_LIMITS as LIMITS} from './ISSUE_REPORT_DIAGNOSTICS_LIMITS';
import {issueReportAffectedRecordSchema} from './IssueReportAffectedRecordSchema';
import {issueReportSeveritySchema} from './IssueReportSeveritySchema';

const RECENT_ERRORS_MAX_ITEMS = 10;
const SINGLE_LINE = /^[^\r\n]*$/;

/**
 * Field that the ticket renders as one labelled line. A CR/LF in such a value would let this
 * untrusted snapshot counterfeit further lines, so reject it outright — the browser has no honest
 * reason to put a newline in a route, a user agent or a timestamp.
 */
export const singleLineText = (maxCharacters: number) =>
  z.string().min(1).max(maxCharacters).regex(SINGLE_LINE);

/**
 * Best-effort diagnostic snapshot attached to a bug report or feature request, gathered client-side
 * (current route, browser, recent errors, and — for the deterministic crash-report trigger — the
 * failing component's stack). Entirely untrusted input from the reporting user's browser: every
 * field is bounded so a malformed or oversized snapshot cannot blow up the ticket payload, and every
 * field except `route`/`occurredAt` is optional so a partial snapshot still reaches the provider.
 * Error names, messages and stacks stay newline-tolerant — real-world errors carry newlines and
 * losing the whole report over telemetry cosmetics is worse than sanitizing them at render time.
 */
export const issueReportDiagnosticsSchema = z
  .object({
    route: singleLineText(LIMITS.route),
    url: singleLineText(LIMITS.url).optional(),
    userAgent: singleLineText(LIMITS.userAgent).optional(),
    language: singleLineText(LIMITS.language).optional(),
    roles: z.array(singleLineText(LIMITS.role)).max(LIMITS.roles).optional(),
    branch: singleLineText(LIMITS.branch).optional(),
    userEmail: singleLineText(LIMITS.userEmail).optional(),
    appVersion: singleLineText(LIMITS.appVersion).optional(),
    envType: singleLineText(LIMITS.envType).optional(),
    occurredAt: singleLineText(LIMITS.occurredAt),
    severity: issueReportSeveritySchema.optional(),
    affectedRecord: issueReportAffectedRecordSchema.optional(),
    recentErrors: z
      .array(
        z.object({
          time: singleLineText(LIMITS.occurredAt),
          source: z.enum(['browser', 'api', 'crash']),
          route: singleLineText(LIMITS.route).optional(),
          message: z.string().min(1).max(LIMITS.errorMessage),
          status: z.number().optional(),
        })
      )
      .max(RECENT_ERRORS_MAX_ITEMS)
      .optional(),
    crash: z
      .object({
        name: z.string().min(1).max(LIMITS.errorName),
        message: z.string().min(1).max(LIMITS.errorMessage),
        stack: z.string().min(1).max(LIMITS.stack).optional(),
        componentStack: z.string().min(1).max(LIMITS.stack).optional(),
        /**
         * Present only when the browser's Sentry client was enabled and captured the crash. Pinned
         * to Sentry's 32-hex event id: it renders as a link label in the ticket, and free text
         * there would let a crafted client dress a Sentry link up as something else.
         */
        sentryEventId: z
          .string()
          .regex(/^[0-9a-f]{32}$/)
          .optional(),
      })
      .optional(),
  })
  .openapi('IssueReportDiagnostics');

export type IssueReportDiagnostics = z.infer<typeof issueReportDiagnosticsSchema>;
